"use client";

import { useEffect } from "react";

function combination(n: number, k: number): number {
  if (k < 0 || k > n) return 0;
  if (k === 0 || k === n) return 1;
  k = Math.min(k, n - k);
  let res = 1;
  for (let i = 0; i < k; i++) {
    res = (res * (n - i)) / (i + 1);
  }
  return res;
}

function minesMultiplier(total: number, mines: number, revealed: number): number {
  // fair multiplier with 1% house edge, matches live-site style (x 1.07 etc)
  if (revealed <= 0) return 1;
  const totalComb = combination(total, revealed);
  const safeComb = combination(total - mines, revealed);
  if (safeComb <= 0) return 1;
  return Math.max(1, (totalComb / safeComb) * 0.99);
}

export function VerbatimClient() {
  useEffect(() => {
    const CHAT_KEY = "cs2skin-chat-open";

    const readChatOpen = (): boolean => {
      try {
        const v = localStorage.getItem(CHAT_KEY);
        // default: open (matches the live site's first-visit state)
        return v === null ? true : v === "1";
      } catch {
        return true;
      }
    };

    const applyChat = (open: boolean) => {
      const r = document.getElementById("verbatim-root");
      // persist on <html> too — layout survives SPA navigations while
      // #verbatim-root is remounted, so hidden stays hidden everywhere
      try {
        document.documentElement.setAttribute(
          "data-chat",
          open ? "open" : "closed"
        );
      } catch {
        /* noop */
      }
      if (!r) return;
      r.setAttribute("data-chat", open ? "open" : "closed");
      // remember the exact captured open classNames per element so
      // restore works on every page even if CSS hashes ever differ
      r.querySelectorAll('[class*="GlobalChat_column"]').forEach((c) => {
        const el = c as HTMLElement;
        if (el.dataset.chatOrig === undefined) {
          el.dataset.chatOrig = el.className.includes("GlobalChat_columnOpen")
            ? el.className
            : "";
        }
        if (open) {
          if (el.dataset.chatOrig && !el.className.includes("GlobalChat_columnOpen")) {
            el.className = el.dataset.chatOrig;
          }
        } else {
          el.className = el.className
            .replace(/GlobalChat_columnOpen__\S+/g, "")
            .trim();
        }
      });
      r.querySelectorAll('[class*="GlobalChat_drawer"]').forEach((d) => {
        const el = d as HTMLElement;
        if (el.dataset.chatOrig === undefined) {
          el.dataset.chatOrig = el.className.includes("GlobalChat_drawerOpen")
            ? el.className
            : "";
        }
        if (open) {
          if (el.dataset.chatOrig && !el.className.includes("GlobalChat_drawerOpen")) {
            el.className = el.dataset.chatOrig;
          }
        } else {
          el.className = el.className
            .replace(/GlobalChat_drawerOpen__\S+/g, "")
            .trim();
        }
      });
    };

    try {
      document.documentElement.removeAttribute("data-locale-pending");
      document.documentElement.removeAttribute("data-chat-open");
      // font var must exist — captured CSS uses var(--font-chakra-petch) everywhere
      const html = document.documentElement;
      html.style.setProperty(
        "--font-chakra-petch",
        '"Chakra Petch", "Chakra Petch Fallback", system-ui, sans-serif'
      );
      html.style.setProperty(
        "--font-chakra-petch-latin",
        '"Chakra Petch", "Chakra Petch Fallback", system-ui, sans-serif'
      );
      html.style.setProperty(
        "--font-chakra-ru",
        '"Chakra Petch", "Chakra Petch Fallback", system-ui, sans-serif'
      );
      html.style.setProperty(
        "--font-inter",
        '"Chakra Petch", "Chakra Petch Fallback", system-ui, sans-serif'
      );
      // NOTE: chat visibility persists in cs2skin-chat-open — never wipe it.
    } catch {
      /* noop */
    }

    const root = document.getElementById("verbatim-root");
    if (!root) return;

    // apply persisted chat visibility BEFORE first paint of toggles
    try {
      applyChat(readChatOpen());
    } catch {
      /* noop */
    }

    /* ── logo: keep the REAL animated webm logo 1:1, just ensure it plays ── */
    const fixLogos = () => {
      root.querySelectorAll("video").forEach((v) => {
        try {
          v.muted = true;
          (v as HTMLVideoElement).autoplay = true;
          const p = v.play();
          if (p && typeof (p as Promise<void>).catch === "function") {
            (p as Promise<void>).catch(() => {});
          }
        } catch {
          /* noop */
        }
        // if the webm 404s, fall back to the PNG at the exact header size
        v.addEventListener(
          "error",
          () => {
            const img = document.createElement("img");
            img.src = "/cdn-next/static/media/logo.5cef2d0a.png";
            img.alt = "CS2Skin";
            img.className = (v.className || "") + " verbatim-logo-fix";
            img.setAttribute("width", "136");
            img.setAttribute("height", "44");
            img.setAttribute("draggable", "false");
            v.replaceWith(img);
          },
          { once: true }
        );
        v.querySelectorAll("source").forEach((s) => {
          s.addEventListener("error", () => {
            const img = document.createElement("img");
            img.src = "/cdn-next/static/media/logo.5cef2d0a.png";
            img.alt = "CS2Skin";
            img.className = (v.className || "") + " verbatim-logo-fix";
            v.replaceWith(img);
          });
        });
      });
      root
        .querySelectorAll(".Header_appTagContainer img:not([src])")
        .forEach((el) => {
          (el as HTMLImageElement).style.display = "none";
        });
      // footer logo safety — captured src is correct, just ensure visible
      root.querySelectorAll(".Footer_logo__txlnn img").forEach((el) => {
        const img = el as HTMLImageElement;
        if (!img.getAttribute("src")) {
          img.src = "/cdn-next/static/media/logo.5cef2d0a.png";
        }
        img.style.objectFit = "contain";
        img.addEventListener("error", () => {
          img.src = "/cdn-next/static/media/logo.5cef2d0a.png";
        });
      });
    };

    /* ── clear chat snapshot (user wants empty chat) ── */
    const clearChat = () => {
      root
        .querySelectorAll('[class*="Chat_messagesList"]')
        .forEach((list) => {
          if (list instanceof HTMLElement) list.innerHTML = "";
        });
      root.querySelectorAll('[class*="ChatMessage_container"]').forEach((n) => {
        if (n.closest('[class*="Chat_messages"]')) n.remove();
      });
      root.querySelectorAll('[class*="Chat_messagesArea"]').forEach((area) => {
        if (
          area instanceof HTMLElement &&
          !area.querySelector("[data-empty-chat]")
        ) {
          const empty = document.createElement("div");
          empty.setAttribute("data-empty-chat", "true");
          empty.className = "verbatim-empty-chat";
          empty.textContent = "No messages yet. Be the first to chat!";
          area.appendChild(empty);
        }
      });
    };

    /* ── clear recent games snapshot on mines (user asked) ── */
    const clearRecentGames = () => {
      const bodies = root.querySelectorAll('[class*="GameBetsTable_body"]');
      if (bodies.length === 0) return;
      bodies.forEach((body) => {
        if (!(body instanceof HTMLElement)) return;
        // only clear the mines recent-games table (has Grids column) — keep
        // other pages intact
        const table = body.closest('[class*="GameBetsTable_table"]');
        const headerText = table?.querySelector(
          '[class*="GameBetsTable_header"]'
        )?.textContent;
        if (headerText && !headerText.includes("Grids")) return;
        body.innerHTML = "";
        if (!body.parentElement?.querySelector("[data-empty-games]")) {
          const empty = document.createElement("div");
          empty.setAttribute("data-empty-games", "true");
          empty.className = "verbatim-empty-games";
          empty.textContent = "No recent games yet. Place a bet to start!";
          body.parentElement?.appendChild(empty);
        }
      });
    };

    /* ── auth store (demo clone — localStorage only, nothing leaves browser) ── */
    type Session = { username: string; email: string; provider: string };
    const USERS_KEY = "cs2skin-users";
    const SESSION_KEY = "cs2skin-session";

    const readUsers = (): Record<string, { username: string; email: string; pass: string }> => {
      try {
        return JSON.parse(localStorage.getItem(USERS_KEY) || "{}");
      } catch {
        return {};
      }
    };
    const writeUsers = (u: Record<string, { username: string; email: string; pass: string }>) => {
      try {
        localStorage.setItem(USERS_KEY, JSON.stringify(u));
      } catch {
        /* noop */
      }
    };
    const getSession = (): Session | null => {
      try {
        const raw = localStorage.getItem(SESSION_KEY);
        return raw ? (JSON.parse(raw) as Session) : null;
      } catch {
        return null;
      }
    };
    const setSession = (s: Session | null) => {
      try {
        if (s) localStorage.setItem(SESSION_KEY, JSON.stringify(s));
        else localStorage.removeItem(SESSION_KEY);
      } catch {
        /* noop */
      }
      try {
        window.dispatchEvent(new CustomEvent("cs2skin-auth-change"));
      } catch {
        /* noop */
      }
    };
    // legacy key from the first auth attempt — migrate so nobody gets logged out
    try {
      const legacy = localStorage.getItem("verbatim-user");
      if (legacy && !localStorage.getItem(SESSION_KEY)) {
        localStorage.setItem(SESSION_KEY, JSON.stringify({ username: legacy, email: "", provider: "email" }));
      }
    } catch {
      /* noop */
    }

    const toast = (msg: string, kind: "ok" | "error" = "ok") => {
      document.querySelectorAll(".verbatim-toast").forEach((n) => n.remove());
      const el = document.createElement("div");
      el.className = "verbatim-toast";
      el.setAttribute("data-kind", kind);
      el.textContent = msg;
      document.body.appendChild(el);
      requestAnimationFrame(() => el.classList.add("verbatim-toast-in"));
      setTimeout(() => {
        el.classList.remove("verbatim-toast-in");
        setTimeout(() => el.remove(), 300);
      }, 2600);
    };

    /* ── auth modal: faithful to cs2skin.com (Steam + Google + Discord + email) ── */
    let authMode: "login" | "signup" = "login";
    const closeAuth = () => {
      document.querySelectorAll(".verbatim-auth-overlay").forEach((n) => n.remove());
      try {
        document.body.style.overflow = "";
      } catch {
        /* noop */
      }
    };
    const showAuthError = (overlay: HTMLElement, msg: string) => {
      const err = overlay.querySelector(".verbatim-auth-error") as HTMLElement | null;
      if (err) {
        err.hidden = false;
        err.textContent = msg;
      }
    };
    const clearAuthError = (overlay: HTMLElement) => {
      const err = overlay.querySelector(".verbatim-auth-error") as HTMLElement | null;
      if (err) {
        err.hidden = true;
        err.textContent = "";
      }
    };
    const finishSocialLogin = (overlay: HTMLElement, provider: "Steam" | "Google" | "Discord", btn: HTMLElement) => {
      btn.setAttribute("disabled", "true");
      const submit = overlay.querySelector(".verbatim-auth-submit") as HTMLButtonElement | null;
      submit?.setAttribute("disabled", "true");
      const label = btn.innerHTML;
      btn.innerHTML = "Connecting…";
      setTimeout(() => {
        const n = Math.floor(1000 + Math.random() * 9000);
        const username = `${provider}User${n}`;
        setSession({ username, email: `${username.toLowerCase()}@demo.local`, provider: provider.toLowerCase() });
        closeAuth();
        applySessionUI();
        toast(`Signed in with ${provider} (demo) — welcome, ${username}!`);
        void label;
      }, 700);
    };
    const doEmailSubmit = (overlay: HTMLElement) => {
      clearAuthError(overlay);
      const get = (name: string) => ((overlay.querySelector(`input[name="${name}"]`) as HTMLInputElement | null)?.value || "").trim();
      const passEl = overlay.querySelector('input[name="password"]') as HTMLInputElement | null;
      const pass = passEl?.value || "";
      const submit = overlay.querySelector(".verbatim-auth-submit") as HTMLButtonElement | null;

      if (authMode === "signup") {
        const username = get("username");
        const email = get("email").toLowerCase();
        const referral = get("referral");
        const userRe = /^[a-zA-Z0-9_.-]{3,24}$/;
        const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
        const mark = (name: string, bad: boolean) => {
          const i = overlay.querySelector(`input[name="${name}"]`) as HTMLInputElement | null;
          if (i) i.dataset.invalid = bad ? "true" : "false";
        };
        if (!userRe.test(username)) {
          mark("username", true);
          showAuthError(overlay, "Username must be 3–24 characters (letters, numbers, _ . -).");
          return;
        }
        mark("username", false);
        if (!emailRe.test(email)) {
          mark("email", true);
          showAuthError(overlay, "Enter a valid email address.");
          return;
        }
        mark("email", false);
        if (pass.length < 6) {
          if (passEl) passEl.dataset.invalid = "true";
          showAuthError(overlay, "Password must be at least 6 characters.");
          return;
        }
        if (passEl) passEl.dataset.invalid = "false";
        const users = readUsers();
        const emailTaken = !!users[email];
        const nameTaken = Object.values(users).some((u) => u.username.toLowerCase() === username.toLowerCase());
        if (emailTaken) {
          showAuthError(overlay, "That email is already registered. Try logging in instead.");
          return;
        }
        if (nameTaken) {
          showAuthError(overlay, "That username is taken. Try another one.");
          return;
        }
        submit?.setAttribute("disabled", "true");
        users[email] = { username, email, pass: btoa(unescape(encodeURIComponent(pass))) };
        writeUsers(users);
        setSession({ username, email, provider: "email" });
        closeAuth();
        applySessionUI();
        toast(referral ? `Account created — referral "${referral}" applied (demo)!` : `Account created — welcome, ${username}!`);
        return;
      }
      // login
      const id = get("identifier").toLowerCase();
      if (!id || !pass) {
        showAuthError(overlay, "Enter your email/username and password.");
        return;
      }
      const users = readUsers();
      const found =
        users[id] ||
        Object.values(users).find(
          (u) => u.username.toLowerCase() === id || u.email.toLowerCase() === id
        );
      if (!found) {
        showAuthError(overlay, "No account found with those details. Try Sign Up first.");
        return;
      }
      if (found.pass !== btoa(unescape(encodeURIComponent(pass)))) {
        showAuthError(overlay, "Incorrect password. Please try again.");
        return;
      }
      submit?.setAttribute("disabled", "true");
      setSession({ username: found.username, email: found.email, provider: "email" });
      closeAuth();
      applySessionUI();
      toast(`Welcome back, ${found.username}!`);
    };
    const openAuth = (mode: "login" | "signup") => {
      authMode = mode;
      closeAuth();
      try {
        document.body.style.overflow = "hidden";
      } catch {
        /* noop */
      }
      const overlay = document.createElement("div");
      overlay.className = "verbatim-auth-overlay";
      overlay.setAttribute("role", "dialog");
      overlay.setAttribute("aria-modal", "true");
      overlay.innerHTML = `
        <div class="verbatim-auth-modal">
          <button class="verbatim-auth-close" aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M1 1l12 12M13 1L1 13"/></svg>
          </button>
          <aside class="verbatim-auth-side">
            <img class="verbatim-auth-logo" src="/cdn-next/static/media/logo.5cef2d0a.png" alt="CS2Skin" />
            <h3 class="verbatim-auth-headline">Pull rare skins.<br />Battle for more.</h3>
            <p class="verbatim-auth-pitch">One account for everything on CS2Skin — your balance, rewards and level travel with you across every game mode.</p>
            <ul class="verbatim-auth-perks">
              <li><span class="verbatim-auth-perk-ico"><svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 6.5L4.5 9L10 3"/></svg></span>Instant withdrawals — skins land in minutes</li>
              <li><span class="verbatim-auth-perk-ico"><svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 6.5L4.5 9L10 3"/></svg></span>Provably fair — verify every roll yourself</li>
              <li><span class="verbatim-auth-perk-ico"><svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 6.5L4.5 9L10 3"/></svg></span>Rakeback &amp; daily rewards on every bet</li>
            </ul>
            <div class="verbatim-auth-stats">
              <div><b>2.4M+</b><span>cases opened</span></div>
              <div><b>$18M</b><span>in skins won</span></div>
              <div><b>3,400</b><span>playing now</span></div>
            </div>
          </aside>
          <div class="verbatim-auth-main">
          <h2 class="verbatim-auth-title">${mode === "login" ? "Welcome back" : "Create your account"}</h2>
          <p class="verbatim-auth-sub">${mode === "login" ? "Log in to battle, open cases and claim rewards." : "Sign up in seconds and get your welcome cases."}</p>
          <div class="verbatim-auth-tabs">
            <button class="verbatim-auth-tab" data-tab="login" data-active="${mode === "login"}">Login</button>
            <button class="verbatim-auth-tab" data-tab="signup" data-active="${mode === "signup"}">Sign Up</button>
          </div>
          <div class="verbatim-auth-social">
            <button class="verbatim-auth-steam" type="button" data-provider="Steam">
              <span class="verbatim-auth-pi verbatim-auth-pi-steam"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.5 1.009 2.455-.397.957-1.497 1.41-2.454 1.012H7.54zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.253 0-2.265-1.014-2.265-2.265z"/></svg></span>
              <span class="verbatim-auth-plabel">Sign in with Steam</span>
              <span class="verbatim-auth-parrow"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>
            </button>
            <div class="verbatim-auth-social-row">
              <button class="verbatim-auth-google" type="button" data-provider="Google">
                <svg width="17" height="17" viewBox="0 0 24 24"><path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.57 5.57 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"/><path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z"/><path fill="#FBBC05" d="M5.27 14.29A7.2 7.2 0 0 1 4.89 12c0-.8.14-1.57.38-2.29V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09z"/><path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42A11.97 11.97 0 0 0 12 0 11.99 11.99 0 0 0 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"/></svg>
                <span class="verbatim-auth-plabel">Google</span>
              </button>
              <button class="verbatim-auth-discord" type="button" data-provider="Discord">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.865-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.74 19.74 0 0 0 3.677 4.37a.07.07 0 0 0-.032.028C.533 9.046-.32 13.58.099 18.058a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.128 12.3 12.3 0 0 1-1.873.891.077.077 0 0 0-.041.107c.36.698.772 1.363 1.225 1.993a.076.076 0 0 0 .084.029 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.055c.5-5.177-.838-9.674-3.549-13.66a.06.06 0 0 0-.031-.029zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
                <span class="verbatim-auth-plabel">Discord</span>
              </button>
            </div>
          </div>
          <div class="verbatim-auth-div">or continue with email</div>
          <p class="verbatim-auth-error" hidden></p>
          <div data-fields="login" ${mode === "login" ? "" : 'hidden'}>
            <div class="verbatim-auth-field">
              <label>Email or username</label>
              <input name="identifier" placeholder="you@example.com" autocomplete="username" maxlength="64" />
            </div>
            <div class="verbatim-auth-field">
              <label>Password</label>
              <div class="verbatim-auth-inputwrap">
                <input name="password" type="password" placeholder="••••••••" autocomplete="current-password" />
                <button class="verbatim-auth-eye" type="button" aria-label="Show password">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                </button>
              </div>
            </div>
            <div class="verbatim-auth-row">
              <span></span>
              <button class="verbatim-auth-forgot" type="button">Forgot password?</button>
            </div>
          </div>
          <div data-fields="signup" ${mode === "signup" ? "" : 'hidden'}>
            <div class="verbatim-auth-field">
              <label>Username</label>
              <input name="username" placeholder="Your username" autocomplete="username" maxlength="24" />
            </div>
            <div class="verbatim-auth-field">
              <label>Email</label>
              <input name="email" type="email" placeholder="you@example.com" autocomplete="email" maxlength="64" />
            </div>
            <div class="verbatim-auth-field">
              <label>Password</label>
              <div class="verbatim-auth-inputwrap">
                <input name="password-signup" type="password" placeholder="Min. 6 characters" autocomplete="new-password" />
                <button class="verbatim-auth-eye" type="button" aria-label="Show password">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                </button>
              </div>
            </div>
            <div class="verbatim-auth-field">
              <label>Referral code <span style="opacity:.55;font-weight:400">(optional)</span></label>
              <input name="referral" placeholder="e.g. VIP" maxlength="24" />
            </div>
          </div>
          <button class="verbatim-auth-submit">${mode === "login" ? "Login" : "Sign Up"}</button>
          <p class="verbatim-auth-switch">${mode === "login" ? 'New here? <button type="button" data-tab="signup">Create an account</button>' : 'Have an account? <button type="button" data-tab="login">Log in</button>'}</p>
          <p class="verbatim-auth-note">By continuing you confirm you are 18+ and accept the <a href="/terms-and-conditions">Terms &amp; Conditions</a>. Demo clone — accounts live only in your browser.</p>
        </div>
      `;
      document.body.appendChild(overlay);

      const setTab = (t: "login" | "signup") => {
        authMode = t;
        overlay.querySelectorAll(".verbatim-auth-tab").forEach((b) => {
          (b as HTMLElement).dataset.active = String((b as HTMLElement).dataset.tab === t);
        });
        const title = overlay.querySelector(".verbatim-auth-title");
        if (title) title.textContent = t === "login" ? "Welcome back" : "Create your account";
        const sub = overlay.querySelector(".verbatim-auth-sub");
        if (sub) sub.textContent = t === "login" ? "Log in to battle, open cases and claim rewards." : "Sign up in seconds and get your welcome cases.";
        const loginF = overlay.querySelector('[data-fields="login"]') as HTMLElement | null;
        const signupF = overlay.querySelector('[data-fields="signup"]') as HTMLElement | null;
        if (loginF) loginF.hidden = t !== "login";
        if (signupF) signupF.hidden = t !== "signup";
        const submit = overlay.querySelector(".verbatim-auth-submit");
        if (submit) submit.textContent = t === "login" ? "Login" : "Sign Up";
        const sw = overlay.querySelector(".verbatim-auth-switch");
        if (sw) sw.innerHTML = t === "login" ? 'New here? <button type="button" data-tab="signup">Create an account</button>' : 'Have an account? <button type="button" data-tab="login">Log in</button>';
        clearAuthError(overlay);
      };

      overlay.addEventListener("click", (ev) => {
        const el = ev.target as HTMLElement;
        if (el === overlay || el.closest(".verbatim-auth-close")) {
          closeAuth();
          return;
        }
        const tab = el.closest("[data-tab]") as HTMLElement | null;
        if (tab) {
          setTab(tab.dataset.tab === "signup" ? "signup" : "login");
          return;
        }
        const eye = el.closest(".verbatim-auth-eye") as HTMLElement | null;
        if (eye) {
          const input = eye.parentElement?.querySelector("input") as HTMLInputElement | null;
          if (input) input.type = input.type === "password" ? "text" : "password";
          return;
        }
        const forgot = el.closest(".verbatim-auth-forgot");
        if (forgot) {
          toast("Password reset is disabled in this demo clone.", "error");
          return;
        }
        const social = el.closest("[data-provider]") as HTMLElement | null;
        if (social) {
          finishSocialLogin(overlay, social.dataset.provider as "Steam" | "Google" | "Discord", social);
          return;
        }
        if (el.closest(".verbatim-auth-submit")) {
          // signup form uses a second password input — mirror it to `password`
          if (authMode === "signup") {
            const sp = overlay.querySelector('input[name="password-signup"]') as HTMLInputElement | null;
            let hidden = overlay.querySelector('input[name="password"]') as HTMLInputElement | null;
            if (sp && !hidden) {
              hidden = document.createElement("input");
              hidden.name = "password";
              hidden.type = "hidden";
              overlay.appendChild(hidden);
            }
            if (hidden && sp) hidden.value = sp.value;
          }
          doEmailSubmit(overlay);
        }
      });
      overlay.addEventListener("keydown", (ev) => {
        if ((ev as KeyboardEvent).key === "Enter" && (ev.target as HTMLElement)?.tagName === "INPUT") {
          ev.preventDefault();
          if (authMode === "signup") {
            const sp = overlay.querySelector('input[name="password-signup"]') as HTMLInputElement | null;
            let hidden = overlay.querySelector('input[name="password"]') as HTMLInputElement | null;
            if (sp && !hidden) {
              hidden = document.createElement("input");
              hidden.name = "password";
              hidden.type = "hidden";
              overlay.appendChild(hidden);
            }
            if (hidden && sp) hidden.value = sp.value;
          }
          doEmailSubmit(overlay);
        }
      });
      overlay.addEventListener("input", () => clearAuthError(overlay));
      const first = overlay.querySelector(authMode === "login" ? 'input[name="identifier"]' : 'input[name="username"]') as HTMLElement | null;
      setTimeout(() => first?.focus(), 50);
    };

    /* ── logged-in header: swap captured Login/Sign Up for balance + account ── */
    let headerOrig: string | null = null;
    const applySessionUI = () => {
      const session = getSession();
      // header
      const slot = root.querySelector(".Header_rightNavigation__YjaFr > div[style]") as HTMLElement | null;
      if (slot) {
        if (headerOrig === null) headerOrig = slot.innerHTML;
        // tear down any previous pill listeners by cloning
        if (session) {
          slot.innerHTML = `
            <div class="verbatim-userpill">
              <div class="verbatim-balance"><img src="/images/coin-2.avif" alt="" />0.00</div>
              <button class="verbatim-deposit" type="button">Deposit</button>
              <div class="verbatim-account">
                <button class="verbatim-account-btn" type="button" aria-haspopup="true">
                  <img src="/images/new-default-avatar.svg" alt="" />
                  <span></span>
                  <svg width="10" height="10" viewBox="0 0 9 9" fill="none"><path d="M1.5 3L4.5 6L7.5 3" stroke="#9DABBD" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </button>
              </div>
            </div>`;
          const name = slot.querySelector(".verbatim-account-btn span");
          if (name) name.textContent = session.username;
          const dep = slot.querySelector(".verbatim-deposit");
          dep?.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            toast("Deposits are disabled in this demo clone.");
          });
          const accBtn = slot.querySelector(".verbatim-account-btn");
          accBtn?.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            const wrap = slot.querySelector(".verbatim-account");
            const existing = slot.querySelector(".verbatim-account-menu");
            if (existing) {
              existing.remove();
              return;
            }
            const menu = document.createElement("div");
            menu.className = "verbatim-account-menu";
            menu.innerHTML = `
              <div class="verbatim-account-email"></div>
              <button type="button" data-act="wallet">Wallet — 0.00</button>
              <button type="button" data-act="logout">Log out</button>`;
            const em = menu.querySelector(".verbatim-account-email");
            if (em) em.textContent = session.email || `@${session.username}`;
            menu.querySelector('[data-act="wallet"]')?.addEventListener("click", (ev) => {
              ev.stopPropagation();
              menu.remove();
              toast("Wallet is disabled in this demo clone.");
            });
            menu.querySelector('[data-act="logout"]')?.addEventListener("click", (ev) => {
              ev.stopPropagation();
              setSession(null);
              applySessionUI();
              toast("Logged out. See you soon!");
            });
            wrap?.appendChild(menu);
            const away = (ev2: MouseEvent) => {
              if (!menu.contains(ev2.target as Node)) {
                menu.remove();
                document.removeEventListener("click", away, true);
              }
            };
            setTimeout(() => document.addEventListener("click", away, true), 0);
          });
        } else if (headerOrig !== null) {
          slot.innerHTML = headerOrig;
          headerOrig = null;
        }
      }
      applySessionToChat(session);
    };

    /* ── chat lock/unlock mirrors live site (guests see "Login to chat") ── */
    const applySessionToChat = (session: Session | null) => {
      const box = root.querySelector(".ChatInputBox_container__NAKnx") as HTMLElement | null;
      if (!box) return;
      const placeholder = root.querySelector(".MentionInput_placeholder__pGtiD") as HTMLElement | null;
      const editable = root.querySelector(".MentionInput_contentEditable__HR187") as HTMLElement | null;
      const sendBtn = root.querySelector(".ChatInputBox_sendButton__njN_m") as HTMLButtonElement | null;
      const emojiBtn = root.querySelector(".ChatInputBox_emojiButton__tdfDF") as HTMLButtonElement | null;
      if (session) {
        box.classList.remove("ChatInputBox_disabled__cXODp");
        if (placeholder) placeholder.textContent = `Message as ${session.username}…`;
        if (editable) {
          editable.setAttribute("contenteditable", "true");
          editable.dataset.authed = "1";
        }
        if (sendBtn) sendBtn.disabled = false;
        if (emojiBtn) emojiBtn.disabled = false;
      } else {
        box.classList.add("ChatInputBox_disabled__cXODp");
        if (placeholder) placeholder.textContent = "Login to chat";
        if (editable) {
          editable.setAttribute("contenteditable", "false");
          editable.dataset.authed = "";
          editable.textContent = "";
        }
        if (sendBtn) sendBtn.disabled = true;
        if (emojiBtn) emojiBtn.disabled = true;
      }
    };
    const sendChatMessage = () => {
      const session = getSession();
      if (!session) {
        openAuth("login");
        return;
      }
      const editable = root.querySelector(".MentionInput_contentEditable__HR187") as HTMLElement | null;
      const text = (editable?.textContent || "").trim();
      if (!text) return;
      const list = root.querySelector(".Chat_messagesList__tMj0k") as HTMLElement | null;
      // drop the "empty chat" placeholder the clone renders for guests
      root.querySelector("[data-empty-chat]")?.remove();
      if (list) {
        const row = document.createElement("div");
        row.innerHTML = `<div class="ChatMessage_container__TKrX_"><div class="ChatMessage_mainRow__FLgbV"><div class="ChatMessage_avatarCol__0My13"><div class="ChatUserAvatar_group__NoenP" style="width:36px;height:46px;--badge-top:26px"><div class="ChatUserAvatar_avatarBorder__PD09M" style="width:36px;height:36px"><img alt="${session.username}" class="ChatUserAvatar_avatar__iKLCB" src="/images/new-default-avatar.svg"></div></div></div><div class="ChatMessage_contentCol__9u8cI"><div class="ChatMessage_headerRow__xK3yZ" style="display:flex;gap:6px;align-items:center"><span style="font-weight:700;font-size:13px;color:#fff">${session.username}</span></div><div class="ChatMessage_text__mB7uA" style="font-size:13px;color:#d7dee8"></div></div></div></div>`;
        const txt = row.querySelector(".ChatMessage_text__mB7uA");
        if (txt) txt.textContent = text;
        list.appendChild(row);
        const area = root.querySelector(".Chat_messagesArea__ZzWPd, .Chat_messagesWrapper__HJZt0");
        if (area instanceof HTMLElement) area.scrollTop = area.scrollHeight;
      }
      if (editable) editable.textContent = "";
    };

    /* ── games dropdown (captured markup never included the menu — build it) ── */
    const closeGames = () => {
      document.querySelectorAll(".verbatim-games-menu").forEach((n) => n.remove());
      document.querySelectorAll('[data-games-open="1"]').forEach((n) => {
        n.removeAttribute("data-games-open");
        n
          .querySelector('img[src*="arrow-down"]')
          ?.setAttribute("style", "color:transparent;transform:rotate(0deg);transition:transform 0.2s ease-in-out");
      });
    };
    const openGames = (anchor: HTMLElement) => {
      closeGames();
      const menu = document.createElement("div");
      menu.className = "verbatim-games-menu";
      menu.setAttribute("role", "menu");
      menu.innerHTML = `
        <a class="verbatim-games-item" href="/case-battle"><img src="/cdn-next/static/media/case-battle-pic.ef4deda0.avif" alt="" /><span>Case Battle</span><em>Vs Players</em></a>
        <a class="verbatim-games-item" href="/roulette"><img src="/cdn-next/static/media/roulette-pic.2f1b7780.avif" alt="" /><span>Roulette</span><em>Win Up to 32X</em></a>
        <a class="verbatim-games-item" href="/cases"><img src="/cdn-next/static/media/cases-pic.314cd493.avif" alt="" /><span>Cases</span><em>Pull Skins</em></a>
        <a class="verbatim-games-item" href="/royale"><img src="/cdn-next/static/media/royale-pic.3ff3c621.avif" alt="" /><span>Royale</span><em>Last One Standing</em></a>
        <a class="verbatim-games-item" href="/mines"><img src="/cdn-next/static/media/mines-pic.147a1512.avif" alt="" /><span>Mines</span><em>Dodge The Bombs</em></a>
        <a class="verbatim-games-item" href="/keno"><img src="/cdn-next/static/media/keno-pic.0e839399.avif" alt="" /><span>Keno</span><em>Pick Your Numbers</em></a>
        <a class="verbatim-games-item" href="/crash"><img src="/cdn-next/static/media/crash-pic.d6716365.avif" alt="" /><span>Crash</span><em>Cash Out In Time</em></a>`;
      const wrap = anchor.closest('[class*="GamesDropDown_dropDownWrapper"]') || anchor.parentElement;
      if (wrap instanceof HTMLElement) {
        wrap.appendChild(menu);
        wrap.setAttribute("data-games-open", "1");
      } else {
        document.body.appendChild(menu);
      }
      anchor
        .querySelector('img[src*="arrow-down"]')
        ?.setAttribute("style", "color:transparent;transform:rotate(180deg);transition:transform 0.2s ease-in-out");
    };

    /* ── mobile drawer (hamburger was dead — now mirrors live nav) ── */
    const closeDrawer = () => {
      document.querySelectorAll(".verbatim-drawer-overlay, .verbatim-drawer").forEach((n) => n.remove());
    };
    const openDrawer = () => {
      closeDrawer();
      const session = getSession();
      const ov = document.createElement("div");
      ov.className = "verbatim-drawer-overlay";
      const dr = document.createElement("div");
      dr.className = "verbatim-drawer";
      dr.setAttribute("role", "dialog");
      dr.setAttribute("aria-label", "Menu");
      dr.innerHTML = `
        <div class="verbatim-drawer-head">
          <img src="/cdn-next/static/media/logo.5cef2d0a.png" alt="CS2Skin" />
          <button class="verbatim-drawer-x" aria-label="Close menu">×</button>
        </div>
        <p class="verbatim-drawer-label">Games</p>
        <a class="verbatim-drawer-link" href="/case-battle">Case Battle</a>
        <a class="verbatim-drawer-link" href="/roulette">Roulette</a>
        <a class="verbatim-drawer-link" href="/cases">Cases</a>
        <a class="verbatim-drawer-link" href="/royale">Royale</a>
        <a class="verbatim-drawer-link" href="/mines">Mines</a>
        <a class="verbatim-drawer-link" href="/keno">Keno</a>
        <a class="verbatim-drawer-link" href="/crash">Crash</a>
        <p class="verbatim-drawer-label">Browse</p>
        <a class="verbatim-drawer-link" href="/market/cs2" hidden>Marketplace</a>
        <a class="verbatim-drawer-link" href="/rewards">Rewards</a>
        <a class="verbatim-drawer-link" href="/leaderboard">Leaderboard</a>
        <a class="verbatim-drawer-link" href="/giveaways">Giveaways</a>
        <div class="verbatim-drawer-auth">
          ${session ? `<button class="verbatim-drawer-login" data-dact="logout">Log out</button><button class="verbatim-drawer-signup" data-dact="deposit">Deposit</button>` : `<button class="verbatim-drawer-login" data-dact="login">Login</button><button class="verbatim-drawer-signup" data-dact="signup">Sign Up</button>`}
        </div>`;
      document.body.appendChild(ov);
      document.body.appendChild(dr);
      ov.addEventListener("click", closeDrawer);
      dr.querySelector(".verbatim-drawer-x")?.addEventListener("click", closeDrawer);
      dr.querySelectorAll("[data-dact]").forEach((b) => {
        (b as HTMLElement).addEventListener("click", () => {
          const act = (b as HTMLElement).dataset.dact;
          closeDrawer();
          if (act === "login") openAuth("login");
          else if (act === "signup") openAuth("signup");
          else if (act === "logout") {
            setSession(null);
            applySessionUI();
            toast("Logged out. See you soon!");
          } else if (act === "deposit") toast("Deposits are disabled in this demo clone.");
        });
      });
    };

    /* ── mines: functional grid sizes 4x4..10x10 + playable board ── */
    const initMines = () => {
      const sizeWrap = root.querySelector('[class*="MinesBetPanel_gridSizeOptions"]');
      const board = root.querySelector('[class*="MinesBoard_board"]') as HTMLElement | null;
      const minesInput = root.querySelector(
        "#mines-number-of-mines"
      ) as HTMLInputElement | null;
      const minesValue = root.querySelector(
        '[class*="MinesBetPanel_numberOfMinesValue"]'
      );
      const minesMult = root.querySelector(
        '[class*="MinesBetPanel_numberOfMinesMultiplier"]'
      );
      const startBtn = root.querySelector(
        '[class*="GameBetPanel_submitButton"], [class*="BetPanel_submitBtn"]'
      ) as HTMLButtonElement | null;
      if (!sizeWrap || !board) return;

      const state = {
        size: 5,
        mines: Number(minesInput?.value) || 3,
        playing: false,
        mineSet: new Set<number>(),
        revealed: new Set<number>(),
        bet: 1,
      };

      // detect current active size from snapshot
      const activeBtn = sizeWrap.querySelector('[class*="gridSizeOptionActive"]');
      if (activeBtn?.textContent) {
        const m = activeBtn.textContent.match(/(\d+)x\1/);
        if (m) state.size = Number(m[1]);
      }

      const template = board.firstElementChild?.cloneNode(true) as HTMLElement | null;

      const paintTileContent = (wrapper: HTMLElement, kind: "cover" | "gem" | "mine" | "exploded") => {
        const content = wrapper.querySelector('[class*="MineTile_content"]');
        if (!(content instanceof HTMLElement)) return;
        if (kind === "cover") {
          // restore original logo mark
          if (template) {
            const t = template.querySelector('[class*="MineTile_content"]');
            if (t) content.innerHTML = t.innerHTML;
          }
          return;
        }
        const img = document.createElement("img");
        img.setAttribute("draggable", "false");
        img.style.cssText = "width:55%;height:55%;object-fit:contain;";
        if (kind === "gem") {
          img.src = "/cdn-next/static/media/diamond.4528d4c1.avif";
          img.alt = "Gem";
        } else if (kind === "mine") {
          img.src = "/cdn-next/static/media/mine.0a30fd8c.avif";
          img.alt = "Mine";
        } else {
          img.src = "/cdn-next/static/media/mine-exploded.d93fa5e5.avif";
          img.alt = "Exploded";
        }
        content.innerHTML = "";
        content.appendChild(img);
      };

      const setWrapperState = (wrapper: HTMLElement, s: "cover" | "gem" | "mine" | "exploded", interactive: boolean) => {
        const base = "MineTile_wrapper__tWsyZ";
        wrapper.className = base;
        if (s === "cover") wrapper.classList.add("MineTile_none__8jPQ2");
        if (s === "gem") {
          wrapper.classList.add("MineTile_selected__EoD1z", "MineTile_selectedDiamond__c18C4");
        }
        if (s === "mine") wrapper.classList.add("MineTile_mine__tJRr7");
        if (s === "exploded") wrapper.classList.add("MineTile_explodedMine__SYNoE");
        wrapper.classList.add(interactive ? "MineTile_interactive__ZpXVS" : "MineTile_disabled__SlaZY");
        wrapper.setAttribute("data-state", s === "cover" ? "none" : s);
        const btn = wrapper.querySelector("button");
        if (btn instanceof HTMLButtonElement) {
          btn.disabled = !interactive;
        }
        paintTileContent(wrapper, s);
      };

      const rebuildBoard = (n: number) => {
        state.size = n;
        state.playing = false;
        state.mineSet.clear();
        state.revealed.clear();
        board.style.setProperty("--mines-board-columns", String(n));
        board.innerHTML = "";
        const total = n * n;
        for (let i = 0; i < total; i++) {
          const node = template
            ? (template.cloneNode(true) as HTMLElement)
            : document.createElement("div");
          if (!template) {
            node.className = "MineTile_wrapper__tWsyZ MineTile_none__8jPQ2 MineTile_disabled__SlaZY";
            node.innerHTML = `<button type="button" class="MineTile_tile__73p_R" data-mines-tile-index="${i}" aria-label="Mine tile ${i + 1}"></button>`;
          }
          const btn = node.querySelector("button");
          if (btn) {
            btn.setAttribute("data-mines-tile-index", String(i));
            btn.setAttribute("aria-label", `Mine tile ${i + 1}`);
          }
          setWrapperState(node, "cover", false);
          board.appendChild(node);
        }
        // mines slider max follows grid (5x5 → 24 = 25-1)
        if (minesInput) {
          minesInput.max = String(n * n - 1);
          if (Number(minesInput.value) > n * n - 1) {
            minesInput.value = String(Math.min(state.mines, n * n - 1));
          }
          state.mines = Number(minesInput.value) || 3;
          syncMinesUI();
        }
        syncStartBtn();
      };

      const syncMinesUI = () => {
        const total = state.size * state.size;
        if (minesValue) minesValue.textContent = String(state.mines);
        if (minesMult) {
          const m = minesMultiplier(total, state.mines, 1);
          minesMult.textContent = `x ${m.toFixed(2)}`;
        }
        const fill = root.querySelector('[class*="MinesNumberOfMinesSlider_fill"]') as HTMLElement | null;
        const thumb = root.querySelector('[class*="MinesNumberOfMinesSlider_thumb"]') as HTMLElement | null;
        if (minesInput && fill && thumb) {
          const min = Number(minesInput.min) || 2;
          const max = Number(minesInput.max) || 24;
          const pct = ((state.mines - min) / Math.max(1, max - min)) * 100;
          fill.style.width = `${pct}%`;
          thumb.style.left = `${pct}%`;
        }
      };

      const syncStartBtn = () => {
        if (!startBtn) return;
        startBtn.removeAttribute("disabled");
        (startBtn as HTMLButtonElement).disabled = false;
        if (state.playing) {
          const total = state.size * state.size;
          const m = minesMultiplier(total, state.mines, state.revealed.size);
          const betInput = root.querySelector("#mines-bet-amount") as HTMLInputElement | null;
          const bet = Number(betInput?.value) || 1;
          startBtn.textContent = `Cashout ${(bet * m).toFixed(2)} (${m.toFixed(2)}x)`;
        } else {
          startBtn.textContent = "Start Game";
        }
      };

      const startGame = () => {
        const total = state.size * state.size;
        if (state.mines >= total) return;
        state.playing = true;
        state.revealed.clear();
        state.mineSet.clear();
        while (state.mineSet.size < state.mines) {
          state.mineSet.add(Math.floor(Math.random() * total));
        }
        Array.from(board.children).forEach((child) => {
          setWrapperState(child as HTMLElement, "cover", true);
        });
        board.classList.add("MinesBoard_interactive__XbDax");
        syncStartBtn();
      };

      const endGame = (hitMine: boolean, hitIndex: number) => {
        state.playing = false;
        board.classList.remove("MinesBoard_interactive__XbDax");
        const total = state.size * state.size;
        for (let i = 0; i < total; i++) {
          const wrapper = board.children[i] as HTMLElement;
          const isMine = state.mineSet.has(i);
          const isRevealed = state.revealed.has(i);
          if (i === hitIndex && hitMine) {
            setWrapperState(wrapper, "exploded", false);
          } else if (isMine) {
            setWrapperState(wrapper, "mine", false);
          } else if (isRevealed) {
            setWrapperState(wrapper, "gem", false);
          } else {
            setWrapperState(wrapper, "cover", false);
          }
        }
        syncStartBtn();
      };

      // wire grid-size buttons — the actual reported bug (4x4,6x6..10x10 did nothing)
      sizeWrap.querySelectorAll("button").forEach((btn) => {
        btn.removeAttribute("disabled");
        (btn as HTMLButtonElement).disabled = false;
        btn.addEventListener("click", () => {
          const m = (btn.textContent || "").match(/(\d+)\s*x\s*(\d+)/i);
          if (!m) return;
          const n = Number(m[1]);
          if (!n || n < 2 || n > 10) return;
          sizeWrap.querySelectorAll("button").forEach((b) => {
            b.classList.remove("MinesBetPanel_gridSizeOptionActive__INQXT");
            b.setAttribute("aria-pressed", "false");
          });
          btn.classList.add("MinesBetPanel_gridSizeOptionActive__INQXT");
          btn.setAttribute("aria-pressed", "true");
          rebuildBoard(n);
        });
      });

      minesInput?.addEventListener("input", () => {
        state.mines = Number(minesInput.value) || 2;
        syncMinesUI();
      });

      // bet amount modifiers (1/2, +1, +100, Max) — make them work
      root.querySelectorAll('[class*="BetPanel_modifierBtn"]').forEach((b) => {
        b.addEventListener("click", () => {
          const betInput = root.querySelector("#mines-bet-amount") as HTMLInputElement | null;
          if (!betInput) return;
          const cur = Number(betInput.value) || 0;
          const label = (b.textContent || "").trim();
          if (label === "1/2") betInput.value = String(Math.max(0.1, cur / 2));
          else if (label === "+1") betInput.value = String(cur + 1);
          else if (label === "+100") betInput.value = String(cur + 100);
          else if (label === "Max") betInput.value = "1000";
          if (state.playing) syncStartBtn();
        });
      });

      startBtn?.addEventListener("click", () => {
        if (state.playing) {
          // cashout = win, keep revealed gems, hide mines as covers
          endGame(false, -1);
          // on cashout show gems only, mines stay covered
          Array.from(board.children).forEach((child, i) => {
            const wrapper = child as HTMLElement;
            if (state.revealed.has(i)) setWrapperState(wrapper, "gem", false);
            else setWrapperState(wrapper, "cover", false);
          });
        } else {
          startGame();
        }
      });

      board.addEventListener("click", (e) => {
        if (!state.playing) return;
        const btn = (e.target as HTMLElement).closest("button[data-mines-tile-index]");
        if (!btn) return;
        const idx = Number(btn.getAttribute("data-mines-tile-index"));
        if (state.revealed.has(idx)) return;
        if (state.mineSet.has(idx)) {
          endGame(true, idx);
          return;
        }
        state.revealed.add(idx);
        const wrapper = btn.closest('[class*="MineTile_wrapper"]') as HTMLElement | null;
        if (wrapper) setWrapperState(wrapper, "gem", true);
        // win when all safe tiles revealed → auto cashout
        const total = state.size * state.size;
        if (state.revealed.size >= total - state.mines) {
          endGame(false, -1);
          Array.from(board.children).forEach((child, i) => {
            const w = child as HTMLElement;
            if (state.revealed.has(i)) setWrapperState(w, "gem", false);
            else setWrapperState(w, "cover", false);
          });
          return;
        }
        syncStartBtn();
      });

      // initial paint
      rebuildBoard(state.size);
    };

    fixLogos();
    clearChat();
    clearRecentGames();
    initMines();
    applySessionUI();

    const onAuthChange = () => applySessionUI();
    window.addEventListener("cs2skin-auth-change", onAuthChange);

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // ── account menu / drawer live inside patched header — let their own handlers run ──
      if (target.closest(".verbatim-account-menu, .verbatim-account-btn, .verbatim-drawer, .verbatim-auth-overlay")) return;

      // ── games menu: close on any click outside trigger + menu (or on item nav) ──
      if (document.querySelector(".verbatim-games-menu")) {
        if (target.closest(".verbatim-games-item")) {
          setTimeout(closeGames, 60);
        } else if (
          !target.closest(".verbatim-games-menu") &&
          !target.closest('[class*="Header_gameLink"]')
        ) {
          closeGames();
        }
      }

      // ── hamburger (mobile menu was dead) ──
      const burger = target.closest('[class*="Header_hamburgerButton"]');
      if (burger) {
        e.preventDefault();
        e.stopPropagation();
        openDrawer();
        return;
      }

      // ── Games dropdown: toggle on trigger click, close on outside click ──
      const gamesTrigger = target.closest('[class*="Header_gameLink"], .verbatim-games-trigger') as HTMLElement | null;
      if (gamesTrigger) {
        e.preventDefault();
        e.stopPropagation();
        if (document.querySelector(".verbatim-games-menu")) {
          closeGames();
        } else {
          openGames(gamesTrigger);
        }
        return;
      }

      // ── Affiliate entry points (header top link + footer "Affiliates") → signup ──
      const aff = target.closest("a, button, span") as HTMLElement | null;
      if (aff) {
        const affLabel = (aff.textContent || "").replace(/\s+/g, " ").trim().toLowerCase();
        if (affLabel === "affiliate" || affLabel === "affiliates") {
          // footer uses a span, header uses a span — never navigate, open signup
          e.preventDefault();
          e.stopPropagation();
          openAuth("signup");
          return;
        }
      }

      // ── auth: header Login / Sign Up (rebuilt buttons + any captured legacy ones) ──
      const authBtn = target.closest(".verbatim-hbtn, button, [role='button']") as HTMLElement | null;
      if (authBtn) {
        const label = (authBtn.textContent || "")
          .replace(/\s+/g, " ")
          .trim()
          .toLowerCase();
        if (label === "login" || label === "log in") {
          e.preventDefault();
          e.stopPropagation();
          openAuth("login");
          return;
        }
        if (label === "sign up" || label === "signup") {
          e.preventDefault();
          e.stopPropagation();
          openAuth("signup");
          return;
        }
      }

      // ── guest chat input → open login (mirrors live "Login to chat" gate) ──
      const chatGate = target.closest('[class*="ChatInputBox_inputSectionSignIn"], [class*="MentionInput_wrapper"]');
      if (chatGate && !getSession()) {
        e.preventDefault();
        e.stopPropagation();
        openAuth("login");
        return;
      }
      const sendBtn = target.closest('[class*="ChatInputBox_sendButton"]');
      if (sendBtn) {
        e.preventDefault();
        e.stopPropagation();
        sendChatMessage();
        return;
      }

      const chatToggle = target.closest(
        '[class*="GlobalChat_toggleButton"], [data-chat-toggle]'
      );
      if (chatToggle) {
        const cols = root.querySelectorAll('[class*="GlobalChat_column"]');
        const drawers = root.querySelectorAll('[class*="GlobalChat_drawer"]');
        // read from <html> (survives SPA nav) falling back to classes
        const htmlState = document.documentElement.getAttribute("data-chat");
        const isOpen =
          htmlState === "open"
            ? true
            : htmlState === "closed"
              ? false
              : Array.from(cols).some((c) =>
                  c.className.includes("GlobalChat_columnOpen")
                );
        const next = !isOpen;
        cols.forEach((c) => {
          if (isOpen)
            c.className = c.className
              .replace(/GlobalChat_columnOpen__\S+/g, "")
              .trim();
          else if (!c.className.includes("columnOpen"))
            c.className += " GlobalChat_columnOpen__CJWX6";
        });
        drawers.forEach((d) => {
          if (isOpen)
            d.className = d.className
              .replace(/GlobalChat_drawerOpen__\S+/g, "")
              .trim();
          else if (!d.className.includes("drawerOpen"))
            d.className += " GlobalChat_drawerOpen___MlJJ";
        });
        // persist — stays hidden across navigations + reloads
        try {
          localStorage.setItem("cs2skin-chat-open", next ? "1" : "0");
        } catch {
          /* noop */
        }
        try {
          document.documentElement.setAttribute(
            "data-chat",
            next ? "open" : "closed"
          );
        } catch {
          /* noop */
        }
        root.setAttribute("data-chat", next ? "open" : "closed");
        // keep classes in sync so the slide animation still plays
        applyChat(next);
        return;
      }

      const accTrigger = target.closest(
        "button[aria-expanded], [data-accordion-trigger]"
      ) as HTMLElement | null;
      if (accTrigger && accTrigger.getAttribute("aria-expanded") !== null) {
        const expanded = accTrigger.getAttribute("aria-expanded") === "true";
        accTrigger.setAttribute("aria-expanded", expanded ? "false" : "true");
        const panel =
          accTrigger.nextElementSibling ||
          accTrigger.parentElement?.querySelector("[data-accordion-panel]");
        if (panel instanceof HTMLElement) {
          panel.hidden = expanded;
        }
        return;
      }

      const tab = target.closest('[role="tab"]') as HTMLElement | null;
      // don't hijack mines bet-panel tabs handled natively — still update selected state
      if (tab && !tab.closest('[class*="GameBetPanel"]')) {
        const list = tab.closest('[role="tablist"]');
        if (list) {
          list
            .querySelectorAll('[role="tab"]')
            .forEach((t) => t.setAttribute("aria-selected", "false"));
          tab.setAttribute("aria-selected", "true");
        }
        return;
      }

      const dismiss = target.closest("[data-dismiss], [data-close-modal]");
      if (dismiss) {
        const modal = dismiss.closest('[role="dialog"], [data-modal]');
        if (modal instanceof HTMLElement) modal.hidden = true;
        return;
      }
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeAuth();
        closeDrawer();
        closeGames();
        document.querySelectorAll('[role="dialog"]').forEach((d) => {
          if (d instanceof HTMLElement && !d.hidden) {
            const auto = d.hasAttribute("data-autoshow");
            if (!auto) d.hidden = true;
          }
        });
        return;
      }
      // logged-in chat: Enter sends (Shift+Enter = newline)
      if (e.key === "Enter" && !e.shiftKey) {
        const t = e.target as HTMLElement | null;
        if (t?.closest?.(".MentionInput_contentEditable__HR187") && (t as HTMLElement).dataset.authed === "1") {
          e.preventDefault();
          sendChatMessage();
        }
      }
    };

    // document-level CAPTURE phase: fires before any captured-site handler can
    // stopPropagation() the click away — guarantees Login/Sign Up/Games always respond
    document.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onKey);

    const lazyImgs = root.querySelectorAll("img[loading='lazy']");
    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            const img = en.target as HTMLImageElement;
            if (img.dataset.src) img.src = img.dataset.src;
            io.unobserve(img);
          }
        });
      });
      lazyImgs.forEach((img) => io.observe(img));
    }

    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("cs2skin-auth-change", onAuthChange);
      closeAuth();
      closeDrawer();
    };
  }, []);

  return null;
}
